import React, { useMemo, useEffect, useState } from "react";
import { toast } from "react-toastify";
import { FaBan } from "react-icons/fa";
import { BtnGeneral } from "../../styles/Buttons";
import DivContainer from "../../components/layouts/DivContainer";
import FullScreenSpinner from "../../components/FullScreenSpinner";
import ConfirmModal from "../../components/Modals/ConfirmModal";
import useLogServices from "../../hooks/logServices/useLogServices";
import SearchableSelect from "../../components/Common/SearchableSelect";
import {
  Page, HeaderCard, Title, FiltersCard, FiltersTitleRow, FiltersTitle,
  FiltersGrid, FieldWrapper, FieldLabel, FieldInput,
  TableCard, TableHeaderRow, TableTitle, TableMeta, TableWrap, Table,
  TableHead, TableBody, Pill, PaginationRow, PaginationButtons, PaginationButton,
  EmptyState, ActionButtons, TdNowrap, SedeTd, SedePill,
  ThCheck, TdCheck, CheckboxInput, ThActions, TdActions, InactivarBtn,
} from "./LogServices.styles";

const fmt = (v) => {
  if (!v) return "—";
  const n = String(v).includes("T") ? v : String(v).replace(" ", "T");
  const d = new Date(n);
  if (Number.isNaN(d.getTime())) return String(v);
  return new Intl.DateTimeFormat("es-CO", { dateStyle: "short", timeStyle: "medium" }).format(d);
};

const jobTone = (c) => {
  const x = String(c || "").toLowerCase();
  if (x === "pacientes") return "info";
  if (x === "master_apim") return "warning";
  if (x === "cohorte") return "success";
  return "default";
};

const LogServices = () => {
  const { state, actions } = useLogServices();
  const { catalogos, loadingCatalogos, logs, pagination, loadingLogs, filtros } = state;
  const { cargarCatalogos, cargarLogs, actualizarFiltro, aplicarFiltros, limpiarFiltros, cambiarPagina, inactivarLogs } = actions;

  const [selectedIds, setSelectedIds] = useState([]);
  const [confirmModalData, setConfirmModalData] = useState({
    isOpen: false,
    title: "",
    message: "",
    targetIds: null,
  });
  const [inactivando, setInactivando] = useState(false);

  useEffect(() => {
    setSelectedIds((prev) => prev.filter((id) => logs.some((r) => r.id === id)));
  }, [logs]);

  useEffect(() => {
    (async () => {
      await cargarCatalogos();
      cargarLogs({ page: 1, force: true });
    })();
  }, [cargarCatalogos, cargarLogs]);

  const allSelected = logs.length > 0 && selectedIds.length === logs.length;

  const toggleSelectAll = () => {
    if (allSelected) {
      setSelectedIds([]);
    } else {
      setSelectedIds(logs.map((r) => r.id));
    }
  };

  const toggleSelectRow = (id) => {
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  const handleInactivarIndividual = (record) => {
    const detalle = `${record.jobCode} (${record.identificacion || record.sourceKey || "ID #" + record.id})`;
    setConfirmModalData({
      isOpen: true,
      title: "Inactivar registro de log",
      message: `¿Está seguro de que desea inactivar este registro?\n\nDetalle: ${detalle}\n\nUna vez inactivado, no se visualizará en la vista.`,
      targetIds: [record.id],
    });
  };

  const handleInactivarSeleccionados = () => {
    if (selectedIds.length === 0) return;
    setConfirmModalData({
      isOpen: true,
      title: "Inactivar registros seleccionados",
      message: `¿Está seguro de que desea inactivar los ${selectedIds.length} registros seleccionados?\n\nUna vez inactivados, no se visualizarán en la vista.`,
      targetIds: [...selectedIds],
    });
  };

  const handleConfirmInactivar = async () => {
    if (!confirmModalData.targetIds || confirmModalData.targetIds.length === 0) return;
    setInactivando(true);
    try {
      const res = await inactivarLogs(confirmModalData.targetIds);
      toast.success(res?.message || "Registros inactivados correctamente");
      setSelectedIds((prev) => prev.filter((id) => !confirmModalData.targetIds.includes(id)));
      setConfirmModalData({ isOpen: false, title: "", message: "", targetIds: null });
    } catch (err) {
      toast.error(err?.message || "Error al inactivar los registros");
    } finally {
      setInactivando(false);
    }
  };

  const sedeLabel = (id) => {
    if (!id) return "General / Global";
    const s = catalogos.sedes.find((x) => String(x.idSede) === String(id));
    if (!s) return "#" + id;
    return s.nombreSede + " — " + s.sourceKey;
  };

  const jobLabel = (c) => {
    const j = catalogos.jobCodes.find((x) => String(x.codigo) === String(c));
    return j ? (j.nombre + " (" + j.codigo + ")") : String(c);
  };

  const refresh = () => {
    toast.promise(cargarLogs({ page: 1, force: true }), {
      pending: "Actualizando registros...",
      success: "Registros actualizados",
      error: "Error actualizando los registros",
    });
  };

  const pages = useMemo(() => {
    const { page, totalPages } = pagination;
    if (totalPages <= 1) return [];
    return Array.from(new Set([1, totalPages, page, page - 2, page - 1, page + 1, page + 2]))
      .filter((p) => p >= 1 && p <= totalPages)
      .sort((a, b) => a - b);
  }, [pagination]);

  const tot = pagination.total;
  const desde = tot === 0 ? 0 : (pagination.page - 1) * pagination.perPage + 1;
  const hasta = Math.min(pagination.page * pagination.perPage, tot);

  if (loadingCatalogos) {
    return (
      <DivContainer>
        <FullScreenSpinner text="Cargando catálogos de sedes y permisos..." />
      </DivContainer>
    );
  }

  return (
    <DivContainer>
      {loadingLogs && <FullScreenSpinner text="Consultando registros de logServices..." />}
      <Page>
        <HeaderCard>
          <Title>Logs del Sincronizador (logServices)</Title>
        </HeaderCard>

        <FiltersCard>
          <FiltersTitleRow>
            <FiltersTitle>Filtros de consulta</FiltersTitle>
            <ActionButtons>
              <BtnGeneral bg="gray" padding="8px 14px" dp="inline-flex" onClick={limpiarFiltros}>
                Limpiar
              </BtnGeneral>
              <BtnGeneral bg="green" padding="8px 14px" dp="inline-flex" onClick={aplicarFiltros}>
                Aplicar filtros
              </BtnGeneral>
            </ActionButtons>
          </FiltersTitleRow>

          <FiltersGrid>
            <FieldWrapper>
              <FieldLabel>Sedes (multi)</FieldLabel>
              <SearchableSelect
                multiple={true}
                searchable={true}
                options={catalogos.sedes}
                value={filtros.sedes || []}
                onValueChange={(val) => actualizarFiltro("sedes", val)}
                disabled={catalogos.sedes.length === 0}
                placeholder="Seleccione..."
                getOptionValue={(s) => String(s.idSede)}
                getOptionLabel={(s) => `${s.nombreSede} — ${s.sourceKey}`}
                sx={{
                  bgcolor: "#ffffff",
                  "& .MuiOutlinedInput-root": {
                    height: "38px",
                    minHeight: "38px",
                    borderRadius: "8px",
                    fontSize: "13px",
                    fontFamily: "var(--font-body)",
                    bgcolor: "#ffffff",
                  },
                  "& .MuiOutlinedInput-notchedOutline": {
                    borderColor: "#cbd5e1",
                  },
                  "&:hover .MuiOutlinedInput-notchedOutline": {
                    borderColor: "#94a3b8",
                  },
                  "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                    borderColor: "#2563eb",
                    boxShadow: "0 0 0 3px rgba(37, 99, 235, 0.15)",
                  },
                }}
                selectSx={{
                  height: "38px",
                  minHeight: "38px",
                  "& .MuiSelect-select": {
                    padding: "8px 12px !important",
                    fontSize: "13px",
                    fontFamily: "var(--font-body)",
                    color: "#0f172a",
                  },
                }}
              />
            </FieldWrapper>

            <FieldWrapper>
              <FieldLabel>Jobs / Tipos (multi)</FieldLabel>
              <SearchableSelect
                multiple={true}
                searchable={true}
                options={catalogos.jobCodes}
                value={filtros.jobCodes || []}
                onValueChange={(val) => actualizarFiltro("jobCodes", val)}
                disabled={catalogos.jobCodes.length === 0}
                placeholder="Seleccione..."
                getOptionValue={(j) => String(j.codigo)}
                getOptionLabel={(j) => jobLabel(j.codigo)}
                sx={{
                  bgcolor: "#ffffff",
                  "& .MuiOutlinedInput-root": {
                    height: "38px",
                    minHeight: "38px",
                    borderRadius: "8px",
                    fontSize: "13px",
                    fontFamily: "var(--font-body)",
                    bgcolor: "#ffffff",
                  },
                  "& .MuiOutlinedInput-notchedOutline": {
                    borderColor: "#cbd5e1",
                  },
                  "&:hover .MuiOutlinedInput-notchedOutline": {
                    borderColor: "#94a3b8",
                  },
                  "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                    borderColor: "#2563eb",
                    boxShadow: "0 0 0 3px rgba(37, 99, 235, 0.15)",
                  },
                }}
                selectSx={{
                  height: "38px",
                  minHeight: "38px",
                  "& .MuiSelect-select": {
                    padding: "8px 12px !important",
                    fontSize: "13px",
                    fontFamily: "var(--font-body)",
                    color: "#0f172a",
                  },
                }}
              />
            </FieldWrapper>

            <FieldWrapper>
              <FieldLabel>Identificación</FieldLabel>
              <FieldInput
                type="text"
                placeholder="Número de documento"
                value={filtros.identificacion || ""}
                onChange={(e) => actualizarFiltro("identificacion", e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") aplicarFiltros(); }}
              />
            </FieldWrapper>

            <FieldWrapper>
              <FieldLabel>Fecha desde</FieldLabel>
              <FieldInput
                type="date"
                value={filtros.fechaDesde || ""}
                onChange={(e) => actualizarFiltro("fechaDesde", e.target.value)}
              />
            </FieldWrapper>

            <FieldWrapper>
              <FieldLabel>Fecha hasta</FieldLabel>
              <FieldInput
                type="date"
                value={filtros.fechaHasta || ""}
                onChange={(e) => actualizarFiltro("fechaHasta", e.target.value)}
              />
            </FieldWrapper>
          </FiltersGrid>
        </FiltersCard>

        <TableCard>
          <TableHeaderRow>
            <TableTitle>Registros ({logs.length})</TableTitle>
            <ActionButtons>
              {selectedIds.length > 0 && (
                <BtnGeneral
                  bg="red"
                  padding="8px 14px"
                  dp="inline-flex"
                  onClick={handleInactivarSeleccionados}
                  disabled={inactivando}
                >
                  <FaBan style={{ marginRight: 6 }} />
                  Inactivar seleccionados ({selectedIds.length})
                </BtnGeneral>
              )}
              <BtnGeneral bg="blue" padding="8px 14px" dp="inline-flex" onClick={refresh}>
                Actualizar
              </BtnGeneral>
            </ActionButtons>
          </TableHeaderRow>

          {logs.length === 0 ? (
            <EmptyState>
              {catalogos.sedes.length === 0
                ? "No tiene sedes accesibles con jobs del sincronizador habilitados. Contacte al administrador."
                : "No se encontraron registros con los filtros seleccionados."}
            </EmptyState>
          ) : (
            <>
              <TableWrap>
                <Table>
                  <TableHead>
                    <tr>
                      <ThCheck>
                        <CheckboxInput
                          type="checkbox"
                          checked={allSelected}
                          onChange={toggleSelectAll}
                          title="Seleccionar todos en la página"
                        />
                      </ThCheck>
                      <th>Fecha</th>
                      <th>Sede</th>
                      <th>Source</th>
                      <th>Identificación</th>
                      <th>Job</th>
                      <th>Mensaje</th>
                      <th>Tabla Origen</th>
                      <ThActions>Acciones</ThActions>
                    </tr>
                  </TableHead>
                  <TableBody>
                  {logs.map((r) => {
                    const s = sedeLabel(r.idSede);
                    return (
                      <tr key={r.id}>
                        <TdCheck>
                          <CheckboxInput
                            type="checkbox"
                            checked={selectedIds.includes(r.id)}
                            onChange={() => toggleSelectRow(r.id)}
                          />
                        </TdCheck>
                        <TdNowrap>{fmt(r.fechaRegistro)}</TdNowrap>
                        <SedeTd>
                          <SedePill tone="info" title={s}>
                            <span>{s}</span>
                          </SedePill>
                        </SedeTd>
                        <td>{r.sourceKey || "—"}</td>
                        <td>{r.identificacion || "—"}</td>
                        <td><Pill tone={jobTone(r.jobCode)}>{r.jobCode}</Pill></td>
                        <td>{r.mensaje}</td>
                        <td>{r.tablaOrigen || "—"}</td>
                        <TdActions>
                          <InactivarBtn
                            type="button"
                            onClick={() => handleInactivarIndividual(r)}
                            disabled={inactivando}
                            title="Inactivar registro"
                          >
                            <FaBan />
                            Inactivar
                          </InactivarBtn>
                        </TdActions>
                      </tr>
                    );
                  })}
                  </TableBody>
                </Table>
              </TableWrap>

              <PaginationRow>
                <TableMeta>
                  Mostrando {logs.length === 0 ? 0 : (desde + " - " + hasta)} de{" "}
                  <strong>{tot.toLocaleString()}</strong>. Página <strong>{pagination.page}</strong> de{" "}
                  <strong>{pagination.totalPages || 1}</strong>.
                </TableMeta>
                <PaginationButtons>
                  <PaginationButton onClick={() => cambiarPagina(1)} disabled={pagination.page <= 1}>«</PaginationButton>
                  <PaginationButton
                    onClick={() => cambiarPagina(Math.max(1, pagination.page - 1))}
                    disabled={pagination.page <= 1}
                  >‹</PaginationButton>
                  {pages.map((p, i, arr) => {
                    const gap = arr[i - 1] && p - arr[i - 1] > 1;
                    return (
                      <React.Fragment key={p}>
                        {gap && <PaginationButton disabled>…</PaginationButton>}
                        <PaginationButton active={p === pagination.page} onClick={() => cambiarPagina(p)}>{p}</PaginationButton>
                      </React.Fragment>
                    );
                  })}
                  <PaginationButton
                    onClick={() => cambiarPagina(pagination.page + 1)}
                    disabled={pagination.page >= (pagination.totalPages || 1)}
                  >›</PaginationButton>
                  <PaginationButton
                    onClick={() => cambiarPagina(pagination.totalPages || 1)}
                    disabled={pagination.page >= (pagination.totalPages || 1)}
                  >»</PaginationButton>
                </PaginationButtons>
              </PaginationRow>
            </>
          )}
        </TableCard>

        <ConfirmModal
          isOpen={confirmModalData.isOpen}
          onClose={() => setConfirmModalData({ isOpen: false, title: "", message: "", targetIds: null })}
          onConfirm={handleConfirmInactivar}
          title={confirmModalData.title}
          message={confirmModalData.message}
          confirmText="Inactivar"
          cancelText="Cancelar"
          confirmColor="red"
          loading={inactivando}
        />
      </Page>
    </DivContainer>
  );
};

export default LogServices;
